import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getValidToken } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function AdminInternshipPostings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [postings, setPostings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [source, setSource] = useState(searchParams.get('source') || '');
  const [activeOnly, setActiveOnly] = useState(searchParams.get('active') !== 'false');
  const [loading, setLoading] = useState(true);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchPostings = useCallback(async () => {
    const token = getValidToken();
    if (!token) { navigate('/admin'); return; }
    setLoading(true);
    try {
      const params = { page, per_page: 25, active_only: activeOnly };
      if (search) params.search = search;
      if (source) params.source = source;
      const res = await axios.get(`${BACKEND_URL}/api/admin/internships/list`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params,
      });
      setPostings(res.data.postings || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (err) {
      console.error('Failed to fetch internships:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, source, activeOnly, navigate]);

  useEffect(() => { fetchPostings(); }, [fetchPostings]);

  useEffect(() => {
    const params = {};
    if (page > 1) params.page = page;
    if (search) params.search = search;
    if (source) params.source = source;
    if (!activeOnly) params.active = 'false';
    setSearchParams(params, { replace: true });
  }, [page, search, source, activeOnly, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const viewDetail = async (id) => {
    const token = getValidToken();
    if (!token) return;
    setDetailLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/internships/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setSelectedPosting(res.data);
    } catch (err) {
      console.error('Failed to fetch detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const formatSalary = (p) => {
    if (!p.ai_salary_min_value && !p.ai_salary_max_value) return null;
    const currency = p.ai_salary_currency || '$';
    const unit = p.ai_salary_unit_text ? `/${p.ai_salary_unit_text.toLowerCase()}` : '';
    if (p.ai_salary_min_value && p.ai_salary_max_value) {
      return `${currency}${p.ai_salary_min_value.toLocaleString()} - ${currency}${p.ai_salary_max_value.toLocaleString()}${unit}`;
    }
    return `${currency}${(p.ai_salary_min_value || p.ai_salary_max_value).toLocaleString()}${unit}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Internship Postings</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{total} postings{activeOnly ? ' (active)' : ''}</p>
          </div>
          <button
            onClick={() => navigate('/admin/platform-settings')}
            className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Back to Settings
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-end">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search title or organization..."
              className="flex-1 px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700">
              Search
            </button>
          </form>
          <select
            value={source}
            onChange={(e) => { setSource(e.target.value); setPage(1); }}
            className="px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">All Sources</option>
            <option value="rapidapi_career_sites">Career Sites</option>
            <option value="rapidapi_job_boards">Job Boards</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => { setActiveOnly(e.target.checked); setPage(1); }}
              className="rounded"
            />
            Active only
          </label>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : postings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No internship postings found. Use "Fetch Internships Now" in Platform Settings to pull listings.
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Organization</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase hidden md:table-cell">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase hidden lg:table-cell">Salary</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase hidden lg:table-cell">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase hidden md:table-cell">Posted</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {postings.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate">{p.title}</div>
                        {p.ai_experience_level && (
                          <span className="text-xs text-gray-500">{p.ai_experience_level} yrs exp</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {p.organization_logo && (
                            <img src={p.organization_logo} alt="" className="w-6 h-6 rounded object-contain" onError={(e) => e.target.style.display='none'} />
                          )}
                          <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[180px] truncate">{p.organization || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {p.cities_derived && Array.isArray(p.cities_derived) ? p.cities_derived.slice(0, 2).join(', ') : '—'}
                          {p.remote_derived && <span className="ml-1 text-xs text-emerald-600 font-medium">Remote</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{formatSalary(p) || '—'}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.source === 'rapidapi_career_sites' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {p.source === 'rapidapi_career_sites' ? 'Career Site' : 'Job Board'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm text-gray-500">{formatDate(p.date_posted)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => viewDetail(p.id)}
                            className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100"
                          >
                            Details
                          </button>
                          {p.url && (
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
                            >
                              Link
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Detail Modal */}
        {selectedPosting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPosting(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white dark:bg-gray-800 border-b px-6 py-4 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">{selectedPosting.title}</h2>
                  <p className="text-sm text-gray-500">{selectedPosting.organization}</p>
                </div>
                <button onClick={() => setSelectedPosting(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>

              {detailLoading ? (
                <div className="p-6 text-center text-gray-500">Loading details...</div>
              ) : (
                <div className="p-6 space-y-5">
                  {/* Key Info Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {selectedPosting.ai_work_arrangement && (
                      <div><span className="font-medium text-gray-500">Work Arrangement</span><p className="text-gray-900 dark:text-white">{selectedPosting.ai_work_arrangement}</p></div>
                    )}
                    {selectedPosting.ai_experience_level && (
                      <div><span className="font-medium text-gray-500">Experience</span><p className="text-gray-900 dark:text-white">{selectedPosting.ai_experience_level} years</p></div>
                    )}
                    {formatSalary(selectedPosting) && (
                      <div><span className="font-medium text-gray-500">Salary</span><p className="text-gray-900 dark:text-white">{formatSalary(selectedPosting)}</p></div>
                    )}
                    {selectedPosting.linkedin_org_industry && (
                      <div><span className="font-medium text-gray-500">Industry</span><p className="text-gray-900 dark:text-white">{selectedPosting.linkedin_org_industry}</p></div>
                    )}
                    {selectedPosting.linkedin_org_employees && (
                      <div><span className="font-medium text-gray-500">Employees</span><p className="text-gray-900 dark:text-white">{selectedPosting.linkedin_org_employees.toLocaleString()}</p></div>
                    )}
                    <div><span className="font-medium text-gray-500">Source</span><p className="text-gray-900 dark:text-white">{selectedPosting.source === 'rapidapi_career_sites' ? 'Career Site' : 'Job Board'}</p></div>
                    <div><span className="font-medium text-gray-500">Posted</span><p className="text-gray-900 dark:text-white">{formatDate(selectedPosting.date_posted)}</p></div>
                    <div><span className="font-medium text-gray-500">Embedding</span><p className="text-gray-900 dark:text-white">{selectedPosting.has_embedding ? 'Yes' : 'No'}</p></div>
                    {selectedPosting.ai_visa_sponsorship && (
                      <div><span className="font-medium text-gray-500">Visa Sponsorship</span><p className="text-emerald-600 font-medium">Yes</p></div>
                    )}
                  </div>

                  {/* Location */}
                  {selectedPosting.cities_derived && Array.isArray(selectedPosting.cities_derived) && selectedPosting.cities_derived.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Location</h3>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedPosting.cities_derived.join(', ')}
                        {selectedPosting.remote_derived && <span className="ml-2 text-emerald-600 font-medium">(Remote)</span>}
                      </p>
                    </div>
                  )}

                  {/* Skills */}
                  {selectedPosting.ai_key_skills && Array.isArray(selectedPosting.ai_key_skills) && selectedPosting.ai_key_skills.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Key Skills</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedPosting.ai_key_skills.map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Benefits */}
                  {selectedPosting.ai_benefits && Array.isArray(selectedPosting.ai_benefits) && selectedPosting.ai_benefits.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Benefits</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedPosting.ai_benefits.map((b, i) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{b}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Requirements Summary */}
                  {selectedPosting.ai_requirements_summary && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Requirements</h3>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedPosting.ai_requirements_summary}</p>
                    </div>
                  )}

                  {/* Responsibilities */}
                  {selectedPosting.ai_core_responsibilities && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Core Responsibilities</h3>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedPosting.ai_core_responsibilities}</p>
                    </div>
                  )}

                  {/* Full Description */}
                  {selectedPosting.description_text && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Full Description</h3>
                      <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-64 overflow-y-auto bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        {selectedPosting.description_text}
                      </div>
                    </div>
                  )}

                  {/* Hiring Manager / Recruiter */}
                  {(selectedPosting.ai_hiring_manager_name || selectedPosting.recruiter_name) && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Contact</h3>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        {selectedPosting.ai_hiring_manager_name && <p>Hiring Manager: {selectedPosting.ai_hiring_manager_name} {selectedPosting.ai_hiring_manager_email && `(${selectedPosting.ai_hiring_manager_email})`}</p>}
                        {selectedPosting.recruiter_name && (
                          <p>Recruiter: {selectedPosting.recruiter_name} {selectedPosting.recruiter_title && `— ${selectedPosting.recruiter_title}`}
                            {selectedPosting.recruiter_url && <a href={selectedPosting.recruiter_url} target="_blank" rel="noopener noreferrer" className="ml-1 text-emerald-600 hover:underline">LinkedIn</a>}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-3 border-t">
                    {selectedPosting.url && (
                      <a
                        href={selectedPosting.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
                      >
                        View Original Listing
                      </a>
                    )}
                    {selectedPosting.organization_url && (
                      <a
                        href={selectedPosting.organization_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                      >
                        Company Page
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
